import { useState } from 'react';

import {
  Box,
  Stack,
  Button,
  Typography,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Table,
  Autocomplete,
  TextField,
} from '@mui/material';

import { AuthLoader } from 'src/pages/auth/component/auth-loader';

import { ConfirmDialog } from 'src/components/custom-dialog';

import { formatPrice } from '../../hook/formatPrice';

type AddBalanceDialogProps = {
  isOpen?: boolean;
  onConfirmHandler?: () => void;
  onCloseHandler?: () => void;
  t: (key: string, options?: any) => string;
  isLoading?: boolean;
  isEstimateLoading?: boolean;
  estimateItem?: any;
  allCoupons?: any[];
  runCheckout?: (amount: number, coupon?: any) => Promise<void>;
  isSuperAdmin?: boolean;
};

export const AddBalanceDialog = ({
  isOpen = false,
  onConfirmHandler = () => {},
  onCloseHandler = () => {},
  t,
  isLoading,
  isEstimateLoading,
  estimateItem,
  allCoupons = [],
  runCheckout,
  isSuperAdmin,
}: AddBalanceDialogProps) => {
  const [selectedCoupon, setSelectedCoupon] = useState<any>(null);
  const [isApplying, setIsApplying] = useState(false);

  const handleClose = () => {
    if (!isLoading) onCloseHandler?.();
  };

  const handleApplyCoupon = async () => {
    if (!estimateItem || !runCheckout || !selectedCoupon) return;
    try {
      setIsApplying(true);
      await runCheckout(estimateItem?.amount || 0, selectedCoupon);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <ConfirmDialog
      maxWidth="lg"
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      open={isOpen}
      onClose={handleClose}
      title={t('ADD_BALANCE')}
      content={
        <>
          {(isLoading || isEstimateLoading || !estimateItem) && <AuthLoader />}

          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('ADD_BALANCE_CONFIRMATION', {
              amount: formatPrice(estimateItem?.total ?? 0),
            })}
          </Typography>

          {estimateItem && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 3,
                mx: 'auto',
                my: 2,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('DESCRIPTION')}</TableCell>
                    <TableCell align="right">{t('PRICE')}</TableCell>
                    <TableCell align="right">{t('DISCOUNT')}</TableCell>
                    <TableCell align="right">{t('TOTAL')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography fontWeight="medium">{t('BALANCE_TOPUP')}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatPrice(estimateItem?.amount || 0)}</TableCell>
                    <TableCell align="right">
                      {estimateItem?.discount_amount > 0
                        ? `-${formatPrice(estimateItem?.discount_amount)}`
                        : formatPrice(0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatPrice(estimateItem?.total || estimateItem?.amount)}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      {t('DISCOUNT')}
                    </TableCell>
                    <TableCell align="right">
                      {formatPrice(
                        estimateItem.line_items.reduce(
                          (acc: any, item: any) => acc + item.discount_amount,
                          0
                        )
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Total Row */}
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      <Typography fontWeight="bold">{t('TOTAL')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="bold">
                        {formatPrice(estimateItem.total)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {estimateItem?.credits_applied > 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="right">
                        <Typography fontWeight="bold">{t('CREDITS_APPLIED')}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold">
                          {'- '}
                          {formatPrice(estimateItem?.credits_applied)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      <Typography fontWeight="bold">{t('PAYABLE_AMOUNT')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="bold">
                        {formatPrice(estimateItem?.amount_due)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Coupon Section */}
              {/* {isSuperAdmin && (
                <Stack direction="row" spacing={2} mt={3} alignItems="center">
                  <Autocomplete
                    sx={{ width: '30%' }}
                    size="small"
                    options={allCoupons}
                    getOptionLabel={(option: any) => option?.code || option?.name || ''}
                    value={selectedCoupon}
                    onChange={(_, newValue) => setSelectedCoupon(newValue)}
                    renderInput={(params) => <TextField {...params} label={t('DISCOUNT_COUPON')} />}
                  />
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleApplyCoupon}
                    disabled={!selectedCoupon}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {t('APPLY')}
                  </Button>
                </Stack>
              )} */}
            </Box>
          )}
        </>
      }
      action={
        <Button variant="contained" color="success" onClick={onConfirmHandler} disabled={isLoading}>
          {t('ADD')}
        </Button>
      }
    />
  );
};
